import { Component, ElementRef, OnDestroy } from '@angular/core';
import {
  faCalendarCheck, faCheck,
  faEye,
  faLock,
  faLockOpen,
  faPlus,
  faTrash,
  faWrench
} from "@fortawesome/free-solid-svg-icons";
import { PAOperation } from "../../classes/operation";
import { PATourPlannerControl } from "../../singletons/pa-tourplanner-control";
import { MapContainer } from "../../map/pa-map";
import { PATechnicianDate } from "../../classes/technician-date";
import { NgIf, NgFor } from '@angular/common';

import { TooltipModule } from '@cloudfactorydk/ng2-tooltip-directive';
import { MatFormField } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { CommonModule } from "@angular/common";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { FaIconComponent, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { PADataControl } from "../../singletons/pa-data-control";

export interface OperationWithStore {
  id: number
  readonly storeId: number
  readonly done?: boolean
  readonly started?: boolean
}

export interface TourIdxShowTour {
  operations: OperationWithStore[]
  getColor(): string
}

export interface TourIdxEditTour extends TourIdxShowTour {
  technicianDate: PATechnicianDate
}

export type TourIdxView = TourIdxShowView | TourIdxEditView

export interface TourIdxData {
  idx: number,
  color: string,
  operation_id: number,
  done?: boolean
  started?: boolean
}

export type ShowTourIdx = ({ tour: TourIdxShowTour } & TourIdxData)
export type EditTourIdx = ({ tour: TourIdxEditTour } & TourIdxData)

export interface TourIdxShowView {
  type: 'show'
  tour_idxs: ShowTourIdx[]
  referenceTours: TourIdxShowTour[]
}

export interface TourIdxEditView {
  type: 'edit'
  tour_idxs: EditTourIdx[]
  tourStopoverEditModule: TourStopoverEditModule
  referenceTours: TourIdxEditTour[]
}

export class TourStopoverEditModule {

  private _notInsertedOperations: PAOperation[] = []
  private _deletedOperations: PAOperation[] = []

  constructor(
    private _mapContainer: MapContainer,
    private _selectedTechnicianDate: PATechnicianDate,
    private _storeId: number,
    private _referenceTours: TourIdxEditTour[]
  ) {
  }

  shownNotInsertedOperations: PAOperation[] = []
  shownDeletedOperations: PAOperation[] = []

  get storeId(): number {
    return this._storeId
  }

  set storeId(id: number) {
    this._storeId = id
  }

  get referenceTours(): TourIdxEditTour[] {
    return this._referenceTours
  }

  set referenceTours(ref_tours: TourIdxEditTour[]) {
    this._referenceTours = ref_tours
  }

  get selectedTechnicianDate(): PATechnicianDate {
    return this._selectedTechnicianDate
  }

  set selectedTechnicianDate(td: PATechnicianDate) {
    this._selectedTechnicianDate = td
    this.updateShownNotInsertedOperations()
    this.updateShownDeletedOperations()
  }

  get notInsertedOperations(): PAOperation[] {
    return this._notInsertedOperations
  }

  set notInsertedOperations(operations: PAOperation[]) {
    this._notInsertedOperations = operations
    this.updateShownNotInsertedOperations()
  }

  get deletedOperations(): PAOperation[] {
    return this._deletedOperations
  }

  set deletedOperations(operations: PAOperation[]) {
    this._deletedOperations = operations
    this.updateShownDeletedOperations()
  }

  get mapContainer(): MapContainer {
    return this._mapContainer
  }

  set mapContainer(map_container: MapContainer) {
    this._mapContainer = map_container
  }

  clickIdx(tour_idx: EditTourIdx) {
    if (!this.isMainMapContainer()) {
      tour_idx.tour.technicianDate.removeOperationsFromNewTour([tour_idx.operation_id])
    } else {
      PATourPlannerControl.Instance.showTourOverview(tour_idx.tour.technicianDate)
    }
  }

  isMainMapContainer(): boolean {
    return this._mapContainer.name == 'map_0'
  }

  makeNotInsertedOperationsUnremovable() {
    if (this._selectedTechnicianDate) {
      for (let operation of this.shownNotInsertedOperations) {
        let store = operation.getStore()
        if (this.storeId == store.id) {
          this._selectedTechnicianDate.insertionConfigMap.get(operation.id).removable = false
          this._selectedTechnicianDate.updateNewTourConfig()
        }
      }
    }
  }

  addStoreOperationsToNewTour() {
    if (this._selectedTechnicianDate) {
      for (let operation of this.shownDeletedOperations) {
        let store = operation.getStore()
        if (this.storeId == store.id) {
          this._selectedTechnicianDate.restoreOperationToNewTour(operation)
        }
      }
    }
  }

  private updateShownNotInsertedOperations() {
    if (this.selectedTechnicianDate) {
      this.shownNotInsertedOperations = this._notInsertedOperations.filter(op => this.selectedTechnicianDate.changedTourContainer.unfittingOperations.includes(op))
    } else {
      this.shownNotInsertedOperations = []
    }
  }

  private updateShownDeletedOperations() {
    if (this.selectedTechnicianDate) {
      this.shownDeletedOperations = this._deletedOperations.filter(op => this.selectedTechnicianDate.changedTourContainer.deletingOperations.includes(op))
    } else {
      this.shownDeletedOperations = []
    }
  }
}

@Component({
  selector: 'hawk-tour-stopover',
  templateUrl: './tour-stopover.component.html',
  styleUrls: ['./tour-stopover.component.scss', './../../styles/common_styles.scss', './../../../_shared/styles/common-styles.scss'],
  standalone: true,
  imports: [NgIf, NgFor, FaIconComponent, TooltipModule, MatFormField, MatSelect, MatOption, CommonModule, MatFormFieldModule, MatSelectModule, FontAwesomeModule]
})
export class TourStopoverComponent implements OnDestroy {

  protected readonly Math = Math
  protected readonly faCalendarCheck = faCalendarCheck;
  protected readonly faLock = faLock;
  protected readonly faPlus = faPlus;
  protected readonly faLockOpen = faLockOpen;
  protected readonly Number = Number;
  protected readonly faEye = faEye;
  protected readonly faTrash = faTrash;

  private _storeId: number
  private _view: TourIdxView

  constructor(
    public elRef: ElementRef
  ) {
  }

  get storeId(): number {
    return this._storeId
  }

  set storeId(id: number) {
    this._storeId = id
    if (this.view.type == 'edit') {
      this.view.tourStopoverEditModule.storeId = id
    }
  }

  get view(): TourIdxView {
    return this._view
  }

  set view(view: TourIdxView) {
    this._view = view
  }

  update(): void {
    if (this.view.referenceTours && this.storeId) {
      this.updateTourIdxs(this.view.referenceTours)
    }
  }

  updateTourIdxs(tours: TourIdxShowTour[]|TourIdxEditTour[]): void {
    let res: (ShowTourIdx|EditTourIdx)[] = []
    for (let tour of tours) {
      let operations = tour.operations
      for (let operation of operations) {
        if (operation.storeId == this.storeId) {
          let op_idx = operations.indexOf(operation) + 1
          res.push({
            tour: tour,
            idx: op_idx,
            operation_id: operation.id,
            color: tour.getColor(),
            done: operation.done,
            started: operation.started
          })
        }
      }
    }
    this.view.tour_idxs = res
  }


  ngOnDestroy(): void {
    console.log('Tour Stopover Component destroyed')
  }

  protected readonly faWrench = faWrench;
  protected readonly faCheck = faCheck;

  tourIdxSelectionChangeIsDisabledForOperation(operation_id: number): boolean {
    let operation = PADataControl.Instance.getOperation(operation_id)
    return !operation || operation.date_fixed || operation.started
  }
}
